<script setup lang="ts">
import { ref } from 'vue';
import SessionBoard from '@/views/app/session/_components/SessionBoard.vue';
import { useSessionStore } from '@/stores/session.ts';
import { useFlags } from '@/composables/use-feature-flags.ts';
import { Flags } from '@pokerly/shared-common';
import SessionToolBar from '@/views/app/session/_components/toolbar/SessionToolBar.vue';
import SessionParticipantsPanel from '@/views/app/session/_components/toolbar/participants/SessionParticipantsPanel.vue';
import SessionRoundsPanel from '@/views/app/session/_components/toolbar/rounds/SessionRoundsPanel.vue';
import ParticipantControlPanel from '@/views/app/session/_components/ParticipantControlPanel.vue';
import SessionBoardResultsPanel from '@/views/app/session/_components/SessionBoardResultsPanel.vue';
import SessionVotingScaleSelector from '@/views/app/session/_components/SessionVotingScaleSelector.vue';
import Panel from '@/components/layout/Panel.vue';
import type { DraftVotingScalePreview } from '@/views/app/session/_components/draft-voting-scale-preview.ts';

const sessionStore = useSessionStore();
const pook52Flag = useFlags(Flags.POK_54_KEEP_TRACK_OF_PLAYED_ROUNDS);
const draftVotingScale = ref<DraftVotingScalePreview | null>(null);
</script>

<template>
    <Panel class="flex h-full relative">
        <div class="h-full flex flex-col w-full overflow-hidden relative">
            <SessionToolBar />
            <SessionParticipantsPanel />
            <div class="flex h-full relative w-full">
                <div class="transition-all duration-300 ease-in-out pt-4 h-full w-full min-w-0">
                    <div class="h-full w-full flex items-center justify-center text-xl font-semibold min-w-0">
                        <div class="flex flex-col h-full w-full transition-all relative min-w-0">
                            <div data-testid="session-board-area" class="grow h-full w-full min-w-0">
                                <div
                                    class="flex h-full w-full min-w-0 min-h-120 items-center justify-center overflow-hidden"
                                >
                                    <div v-if="sessionStore.isSessionCreated" class="flex w-full min-w-0 flex-col">
                                        <SessionVotingScaleSelector @preview-change="draftVotingScale = $event" />
                                    </div>
                                    <div
                                        v-else
                                        class="flex min-w-0 items-center justify-center transition-all duration-300 ease-in-out px-16"
                                    >
                                        <SessionBoard :participants="sessionStore.voters" />
                                    </div>
                                    <div
                                        data-testid="session-results-panel"
                                        :data-open="sessionStore.isRoundClosed"
                                        class="group/session-results relative min-w-0 shrink-0 overflow-hidden transition-all duration-300 w-0 data-[open=true]:overflow-visible data-[open=true]:w-[min(44%,36rem)]"
                                    >
                                        <div
                                            class="relative h-full min-w-136 w-full py-4 pr-4 opacity-0 translate-x-full transition-all duration-300 ease-in-out group-data-[open=true]/session-results:opacity-100 group-data-[open=true]/session-results:translate-x-0"
                                        >
                                            <SessionBoardResultsPanel />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="relative w-full h-48 shrink-0">
                                <ParticipantControlPanel :draft-voting-scale="draftVotingScale" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <SessionRoundsPanel v-if="pook52Flag.enabled && sessionStore.isSessionActive" />
    </Panel>
</template>
